import { Cloud, Search, MapPin, Loader2, AlertTriangle, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { WeatherData, WeatherAlert, HourlyForecast } from '@/types/headerFeed';
import { useWeatherNotifications } from '@/hooks/useWeatherNotifications';
import { useEffect } from 'react';
import { format } from 'date-fns';

interface WeatherChipProps {
  weather: WeatherData | null;
  loading?: boolean;
  onClick: () => void;
  className?: string;
}

function getSeverityColor(severity: WeatherAlert['severity']): string {
  switch (severity) {
    case 'extreme': return 'text-red-500 bg-red-500/20';
    case 'severe': return 'text-orange-500 bg-orange-500/20';
    case 'moderate': return 'text-yellow-500 bg-yellow-500/20';
    default: return 'text-blue-500 bg-blue-500/20';
  }
}

function HourlyForecastPreview({ hourly }: { hourly: HourlyForecast[] }) {
  if (!hourly || hourly.length === 0) return null;
  
  return (
    <div className="space-y-1.5 mt-2 pt-2 border-t border-border/50">
      <p className="text-xs font-medium text-muted-foreground">Next Hours</p>
      <div className="flex gap-3">
        {hourly.slice(0, 5).map((hour, i) => (
          <div key={i} className="text-center min-w-[40px]">
            <p className="text-xs text-muted-foreground">
              {format(new Date(hour.time), 'ha')}
            </p>
            <p className="text-sm font-semibold">{hour.tempF}°</p>
            <p className="text-[10px] text-muted-foreground truncate w-10">
              {hour.summary.split(' ')[0]}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertIndicator({ alerts }: { alerts: WeatherAlert[] }) {
  if (!alerts || alerts.length === 0) return null;
  
  const highestSeverity = alerts[0]?.severity || 'minor';
  const colorClass = getSeverityColor(highestSeverity);
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn(
            "flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium animate-pulse",
            colorClass
          )}>
            <AlertTriangle className="h-3 w-3" />
            <span className="hidden sm:inline">{alerts.length}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-2">
            <p className="font-semibold">Weather Alerts ({alerts.length})</p>
            {alerts.slice(0, 3).map((alert, i) => (
              <div key={i} className="text-xs">
                <span className={cn(
                  "inline-block px-1 rounded mr-1 capitalize",
                  getSeverityColor(alert.severity)
                )}>
                  {alert.severity}
                </span>
                {alert.event}
              </div>
            ))}
            {alerts.length > 3 && (
              <p className="text-xs text-muted-foreground">
                +{alerts.length - 3} more alerts
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function NotificationToggle() {
  const { permission, isEnabled, requestPermission, disableNotifications, isSupported } = useWeatherNotifications();
  
  if (!isSupported) return null;
  
  const handleToggle = async () => {
    if (isEnabled) {
      disableNotifications();
      toast.info('Weather alerts disabled');
    } else {
      const granted = await requestPermission();
      if (granted) {
        toast.success('Weather alerts enabled! You\'ll be notified of severe weather.');
      } else if (permission === 'denied') {
        toast.error('Notifications blocked by browser. Enable them in your browser settings.');
      } else {
        toast.warning('Notification permission not granted. Please allow notifications in your browser.');
      }
    }
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={handleToggle}
            aria-label={isEnabled && permission === 'granted' ? 'Disable weather alerts' : 'Enable weather alerts'}
          >
            {isEnabled && permission === 'granted' ? (
              <Bell className="h-3 w-3 text-primary" />
            ) : (
              <BellOff className="h-3 w-3 text-muted-foreground" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {isEnabled && permission === 'granted' 
            ? 'Severe weather alerts enabled - click to disable' 
            : 'Enable notifications for severe weather alerts'}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function WeatherChip({ weather, loading, onClick, className }: WeatherChipProps) {
  const { checkAndNotifyAlerts, isEnabled, permission } = useWeatherNotifications();
  
  // Check for alerts when weather data changes
  useEffect(() => {
    if (weather?.alerts && isEnabled && permission === 'granted') {
      checkAndNotifyAlerts(weather.alerts);
    }
  }, [weather?.alerts, checkAndNotifyAlerts, isEnabled, permission]);
  
  if (loading) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className={cn("gap-2 text-sm shrink-0", className)}
        disabled
        aria-label="Loading weather"
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="hidden sm:inline">Loading...</span>
      </Button>
    );
  }
  
  if (!weather) {
    return (
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onClick}
          className={cn("gap-2 text-sm shrink-0", className)}
          aria-label="Set weather location"
        >
          <MapPin className="h-4 w-4" />
          <span className="hidden sm:inline">Set Location</span>
          <Search className="h-3 w-3 opacity-60" />
        </Button>
        <NotificationToggle />
      </div>
    );
  }
  
  const hasAlerts = weather.alerts && weather.alerts.length > 0;
  
  return (
    <div className="flex items-center gap-1">
      {hasAlerts && <AlertIndicator alerts={weather.alerts!} />}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClick}
              className={cn(
                "gap-2 text-sm shrink-0 hover:bg-muted/50",
                hasAlerts && "pr-2",
                className
              )}
              aria-label={`Weather: ${weather.tempF}°F ${weather.summary} in ${weather.locationLabel}${hasAlerts ? `. ${weather.alerts!.length} active alerts.` : ''} Click to change location.`}
            >
              <Cloud className="h-4 w-4" />
              <span className="hidden sm:inline truncate max-w-24">
                {weather.locationLabel}
              </span>
              <span className="font-medium">{weather.tempF}°F</span>
              <Search className="h-3 w-3 opacity-60" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs p-3">
            <div className="space-y-1">
              <p className="font-semibold">{weather.locationLabel}</p>
              <p className="text-2xl font-bold">{weather.tempF}°F</p>
              <p className="text-sm text-muted-foreground">{weather.summary}</p>
              {weather.hourly && <HourlyForecastPreview hourly={weather.hourly} />}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <NotificationToggle />
    </div>
  );
}
